import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { loadAccessoryImagePlaceholder, loadDeviceImagePlaceholder } from '@models/functions.model';
import {
  BulkOrderInfo,
  QuantitySelectionType,
  ShoppingCartData,
  ShoppingCartItem
} from '@models/shopping-cart.model';
import {
  CART_ITEM_TYPE_ACCESSORY,
  CART_ITEM_TYPE_DEVICE,
  CART_ITEM_TYPE_FEATURE,
  CART_ITEM_TYPE_PLAN,
  OrderTypeSettings,
  StoreQueryParams
} from '@models/shopping.model';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityStateService } from '@services/activity/state/activity-state.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { BaseBadgeComponent } from '@shared-components/badge/base-badge/base-badge.component';
import { Subject, Subscription } from 'rxjs';

export const ITEM_QUANTITY: QuantitySelectionType[] = [
  { name: 1, value: 1 },
  { name: 2, value: 2 },
  { name: 3, value: 3 },
  { name: 4, value: 4 },
  { name: 5, value: 5 },
  { name: 6, value: 6 }
];

@Component({
  selector: 'ts-cart-items',
  templateUrl: './cart-items.component.html',
  imports: [CommonModule, ReactiveFormsModule, TranslationPipe, BaseBadgeComponent]
})
export class CartItemsComponent implements OnInit, OnDestroy {
  @Input() shopping_cart: ShoppingCartData;
  @Input() shopping_items_price: ShoppingCartData = {
    shopping_cart_items: [],
    orderTypeId: 0
  };
  @Output() update_cart: Subject<string | boolean> = new Subject<string | boolean>();
  @Output() bulk_form_valid: EventEmitter<{ valid: boolean; qty?: number }> = new EventEmitter();
  @Output() is_bulk_order: EventEmitter<boolean> = new EventEmitter();
  cart_items: ShoppingCartItem[] = [];
  cart_item_types: string[] = [
    CART_ITEM_TYPE_DEVICE,
    CART_ITEM_TYPE_PLAN,
    CART_ITEM_TYPE_FEATURE,
    CART_ITEM_TYPE_ACCESSORY
  ];
  accessory_selection: QuantitySelectionType[] = ITEM_QUANTITY;
  bulk_order_info: BulkOrderInfo = { is_bulk_order: false };
  keep_plan: boolean | undefined;
  bulk_order_form: FormGroup = new FormGroup({
    bulkQuantity: new FormControl(null, [Validators.required, Validators.pattern('^[0-9]*$')])
  });
  private subscriptions: Subscription = new Subscription();

  constructor(
    private activity_state_svc: ActivityStateService,
    private router: Router,
    private shopping_svc: ShoppingService
  ) {
    if (!this.shopping_items_price.orderTypeId)
      this.shopping_items_price.orderTypeId = this.activity_state_svc.orderTypeId()!;
  }

  ngOnInit(): void {
    if (this.shopping_cart?.quantity) {
      this.bulk_order_form.controls['bulkQuantity'].setValue(this.shopping_cart.quantity);
      this.bulk_form_valid.emit({
        valid: true,
        qty: this.bulk_order_form.controls['bulkQuantity'].value
      });
    }

    this.retrieveBulkOrderSettings();
    this.loadCartItems();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadCartItems(element_id?: string): void {
    this.subscriptions.add(
      this.shopping_svc.getShoppingCartSummary().subscribe({
        next: (res: ShoppingCartData) => {
          this.cart_items = this.updateCartItemsOrder(res?.shopping_cart_items);
          this.keep_plan = res.keep_plan;

          if (element_id) {
            let qty_element: HTMLElement | null = document.getElementById(element_id);
            qty_element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
  }

  isDisplayEdit(cart_item: ShoppingCartItem): boolean {
    const card_details: StoreQueryParams = JSON.parse(cart_item.edit_link_payload as string);
    if (cart_item.type === this.cart_item_types[1])
      return card_details.number_of_plans! > 1 ? true : false;
    else if (cart_item.type === this.cart_item_types[2])
      return card_details.number_of_features! > 1 ? true : false;

    return true;
  }

  onSelectChange(cart_item: ShoppingCartItem, quantity: string, html_id: string): void {
    this.shopping_cart.shopping_cart_items.map((item: ShoppingCartItem) => {
      if (cart_item.id == item.id) item.quantity = Number(quantity);
    });

    this.shopping_items_price.shopping_cart_items.map((item: ShoppingCartItem) => {
      if (cart_item.id == item.id) item.quantity = Number(quantity);
    });

    this.subscriptions.add(
      this.shopping_svc.sendShoppingItems(this.shopping_cart).subscribe({
        next: (res: ShoppingCartData) => {
          this.shopping_cart.shopping_cart_items = res.shopping_cart_items;
          this.loadCartItems(html_id);
          this.update_cart.next(true);
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
  }

  removeCartItem(cart_items: ShoppingCartItem): void {
    this.subscriptions.add(
      this.shopping_svc.sendShoppingItems(this.shopping_cart).subscribe({
        next: (res: ShoppingCartData) => {
          this.subscriptions.add(
            this.shopping_svc.deleteCartItem(cart_items).subscribe({
              next: (response: ShoppingCartData) => {
                this.loadCartItems();
                this.shopping_cart = response;
                this.update_cart.next('refresh_cart_list');
              }
            })
          );
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
  }

  navigateToDetailsPage(cart_items: ShoppingCartItem): void {
    this.subscriptions.add(
      this.shopping_svc.sendShoppingItems(this.shopping_cart).subscribe({
        next: (res: ShoppingCartData) => {
          if (cart_items.type === this.cart_item_types[3]) {
            let accessory_params = JSON.parse(cart_items.edit_link_payload as string);
            this.router.navigate([`/shop/${this.activity_state_svc.orderTypeId()}/accessories`], {
              queryParams: {
                deviceId: accessory_params.deviceId,
                lineId: accessory_params.lineId,
                ct: `${cart_items.additional_info?.category_id}&${cart_items.additional_info?.category}`
              }
            });
          } else {
            switch (res.orderTypeId) {
              case 11:
                this.router.navigate([`/shop/11/manage-service`]);
                break;
              default:
                let cart_item_details = JSON.parse(cart_items.edit_link_payload as string);
                this.router.navigate(
                  [`/shop/${this.activity_state_svc.orderTypeId()}/device-details`],
                  {
                    queryParams: {
                      carrierId: cart_item_details.carrierId,
                      name: cart_item_details.name,
                      filterConfigId: cart_item_details.filterConfigId,
                      filters: cart_item_details.filters,
                      categories: cart_item_details.categories,
                      countryId: cart_item_details.countryId,
                      colorId: cart_item_details.colorId,
                      deviceId: cart_item_details.deviceId,
                      keep_plan: this.keep_plan,
                      isBpDevice: cart_item_details.isBpDevice
                    }
                  }
                );
                break;
            }
          }
        },
        error: (err: HttpErrorResponse) => {}
      })
    );
  }

  generatePlaceholderImage(event: Event, cart_item: ShoppingCartItem): string {
    const image_url =
      cart_item.type === this.cart_item_types[0]
        ? loadDeviceImagePlaceholder(cart_item!.additional_info!.category!, event)
        : cart_item.type === this.cart_item_types[3]
          ? loadAccessoryImagePlaceholder(cart_item!.additional_info!.category!, event)
          : '';
    return image_url;
  }

  hasRemovableCartItems(cart_item: ShoppingCartItem): boolean {
    return (
      (cart_item.type === this.cart_item_types[2] &&
        !(cart_item.additional_info?.included === true || this.shopping_cart.keep_plan === true)) ||
      (![14].includes(this.shopping_cart.orderTypeId) &&
        cart_item.type === this.cart_item_types[3]) ||
      ([14].includes(this.shopping_cart.orderTypeId) &&
        this.shopping_cart.shopping_cart_items.length > 1)
    );
  }

  bulk_quantity_entered(quantity: number): void {
    this.bulk_form_valid.emit({ valid: this.bulk_order_form.valid, qty: quantity });
  }

  private updateCartItemsOrder(original_cart_items: ShoppingCartItem[]): ShoppingCartItem[] {
    original_cart_items.sort((a, b) => {
      return this.cart_item_types.indexOf(a.type) - this.cart_item_types.indexOf(b.type);
    });
    return original_cart_items;
  }

  private retrieveBulkOrderSettings(): void {
    this.subscriptions.add(
      this.shopping_svc.getPlanSettings(this.activity_state_svc.orderTypeId()).subscribe({
        next: (res: OrderTypeSettings) => {
          this.bulk_order_info = {
            is_bulk_order: !!(res.bulk_quantity_min_limit && res.bulk_quantity_max_limit),
            min_value: res.bulk_quantity_min_limit,
            max_value: res.bulk_quantity_max_limit
          };

          if (!this.bulk_order_info.is_bulk_order) this.bulk_form_valid.emit({ valid: true });
          else if (this.bulk_order_info.min_value && this.bulk_order_info.max_value) {
            this.is_bulk_order.emit(true);
            this.bulk_order_form.controls['bulkQuantity'].addValidators([
              Validators.min(this.bulk_order_info.min_value),
              Validators.max(this.bulk_order_info.max_value)
            ]);
          }
        }
      })
    );
  }
}
